import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { PACKAGE_FEATURES } from "@/config/package";

const SubscriptionContext = createContext({
  package: "core",
  status: "active",
  isLocked: false,
  nextDueDate: null,
  monthlyAmount: null,
  features: new Set(),
  hasFeature: () => true,
  refresh: () => {},
  ready: false,
});

export const SubscriptionProvider = ({ children }) => {
  // Default to Core feature set on first paint so we don't briefly expose
  // Pro-only UI to a Core customer before /api/subscription/info resolves.
  // Pro customers will see a brief flash of Core UI then unlock — acceptable.
  const [state, setState] = useState({
    package: "core",
    status: "active",
    isLocked: false,
    nextDueDate: null,
    monthlyAmount: null,
    features: PACKAGE_FEATURES.core,
    ready: false,
  });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Global axios interceptor: surface 423 (locked) and 403 feature_locked errors as toasts.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const data = err?.response?.data;
        if (err?.response?.status === 423 && data?.code === "subscription_locked") {
          import("sonner").then(({ toast }) => toast.error(data.detail || "Subscription overdue — edits locked."));
        } else if (err?.response?.status === 403 && data?.code === "feature_locked") {
          import("sonner").then(({ toast }) => toast.error(data.detail || "This feature requires a Pro subscription."));
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const fetchInfo = useCallback(async () => {
    try {
      // First, ask the backend to recompute status (active/grace/expired) based on date.
      await axios.post(`${API}/subscription/check-expiry`).catch(() => {});
      const { data } = await axios.get(`${API}/subscription/info`);
      // Defensive: if the backend ever returns an invalid package value, KEEP the
      // last-known state instead of silently defaulting to a different package
      // (which would visibly downgrade the customer's UI from Pro → Core).
      const validPkgs = Object.keys(PACKAGE_FEATURES);
      if (!data || !validPkgs.includes(data.package)) {
        setState((s) => ({ ...s, ready: true }));
        return;
      }
      setState({
        package: data.package,
        status: data.status || "active",
        isLocked: !!data.is_locked,
        nextDueDate: data.next_due_date,
        monthlyAmount: data.monthly_amount,
        currency: data.currency || "INR",
        features: PACKAGE_FEATURES[data.package],
        ready: true,
      });
    } catch (e) {
      // Transient failure (network blip, brief backend restart). KEEP the
      // previous valid state to avoid a flicker from Pro → Core that the
      // customer would notice. We just mark the context as ready.
      setState((s) => ({ ...s, ready: true }));
    }
  }, [API]);

  useEffect(() => {
    fetchInfo();
    // Re-check every 5 minutes — picks up subscription changes without page reload.
    const t = setInterval(fetchInfo, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchInfo]);

  const hasFeature = useCallback((feat) => state.features.has(feat), [state.features]);

  return (
    <SubscriptionContext.Provider value={{ ...state, hasFeature, refresh: fetchInfo }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
